import style from './style.module.css';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { PopConfirm, Button, Col, Row, Notification } from 'fave-ui';
import Form from '../../../components/common/form/Form';
import { Input } from '../../../components/common/form/Input';
import SubmitButton from '../../../components/common/form/SubmitButton';
import {
  AddCashbackCampaignPayload,
  useAddEcardCampaignMutation,
} from '../../../services/CashbackCampaign/useFetchFLPCashbackListing';
import { cashbackCampaignTabs } from '..';
import { DatePicker } from '../../../components/common/form';
import { subDays } from 'date-fns';
import ImageUpload from '../../../components/common/form/ImageUpload';
import { appendFormData, formatDate } from '../../../utils/utilFunctions';

const CAMPAIGN_TYPE = 'BKE::ECard';

const ECardCampaignFormSchema = Yup.object().shape({
  campaign_type: Yup.string().required(
    'Please select a campaign type to continue',
  ),
  campaign_name: Yup.string().required('Please give your campaign a name'),
  start_date: Yup.date()
    .typeError('Please select a start date')
    .min(subDays(new Date(), 1), 'Start date cannot be in the past')
    .required('Please select a start date'),
  end_date: Yup.date()
    .typeError('Please select a end date')
    .min(Yup.ref('start_date'), "End date can't be before start date")
    .required('Please select a end date'),
  description: Yup.string()
    .required('Please enter a description')
    .max(50, 'Description must be less than 50 characters'),
  image: Yup.array()
    .of(Yup.mixed().nullable().required('Image is required'))
    .min(1, 'Please upload an image'),
});

const initialValues: AddCashbackCampaignPayload = {
  campaign_type: CAMPAIGN_TYPE,
  campaign_name: '',
  start_date: '',
  description: '',
  end_date: '',
  image: [],
};

const successNotification = () => {
  Notification.success({
    message: 'New Campaign Submitted!',
    description: 'A new BKE eCard Giveaway Campaign has been created',
  });
};

export default function ECardCampaignForm() {
  const { mutateAsync: addCashbackCampaign } = useAddEcardCampaignMutation();

  const campaignTab = cashbackCampaignTabs.find(
    n => n.funder_type === CAMPAIGN_TYPE,
  )?.key;

  const redirectUrl = `/admin/merchandise_and_campaign/cashback_campaigns${campaignTab ? `?tab=${campaignTab}` : ''}`;

  const navigate = useNavigate();

  const handleSubmit = async (
    values: AddCashbackCampaignPayload,
    { setSubmitting }: FormikHelpers<AddCashbackCampaignPayload>,
  ) => {
    const payload = {
      ...values,
      funder_type: CAMPAIGN_TYPE,
      start_date: formatDate(values.start_date, 'yyyy-MM-d'),
      end_date: formatDate(values.end_date, 'yyyy-MM-d'),
      image:
        values.image &&
        values.image.length > 0 &&
        values.image[0].originFileObj != undefined
          ? values.image[0].originFileObj
          : [],
    };

    const formData = appendFormData(payload);

    await addCashbackCampaign(
      { formData },
      {
        onSuccess: () => {
          successNotification();

          navigate(redirectUrl);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={ECardCampaignFormSchema}
      onSubmit={handleSubmit}
      validateOnMount
      validateOnChange
      validateOnSchemaChange
    >
      <Row gutter={[16, 16]}>
        <p className={style.section}>2. Campaign Detail</p>

        <Col span={24}>
          <Input
            name="campaign_name"
            label="* Campaign Name"
            placeholder="Enter campaign name"
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>

        <Col span={24}>
          <DatePicker
            name="start_date"
            label="Start Date"
            allowClear
            bordered
          />
        </Col>

        <Col span={24}>
          <DatePicker name="end_date" label="End Date" allowClear bordered />
        </Col>

        <Col span={24}>
          <Input
            type="textArea"
            name="description"
            label="* Description"
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>

        <Col span={24}>
          <ImageUpload
            name="image"
            label="Upload campaign masthead - PNG (120px x 44px)"
            types={['image/png']}
            imgNumber={1}
          />
        </Col>

        <Col span={24}>
          <SubmitButton disableIfNotValid disableIfNotDirty>
            {(_, isSubmitting) => (isSubmitting ? 'Submitting...' : 'Save')}
          </SubmitButton>
          <PopConfirm
            title="Are you sure you want to cancel? All progress will be lost for the current campaign."
            onConfirm={() => navigate(redirectUrl)}
            okText="Yes, I am sure"
            cancelText="Go Back"
          >
            <Button type="primary" size="large" className={style.cancelBtn}>
              Cancel
            </Button>
          </PopConfirm>
        </Col>
      </Row>
    </Form>
  );
}
